import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ArrowLeft, Plus, Edit2, Save, X } from 'lucide-react';

const ObjetivoDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [objective, setObjective] = useState(null);
  const [keyResults, setKeyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    description: '',
    status: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [objRes, krsRes] = await Promise.all([
        api.get(`/objectives/${id}`),
        api.get(`/objectives/${id}/key-results`)
      ]);
      
      const objData = objRes?.data || objRes;
      setObjective(objData);
      setKeyResults(Array.isArray(krsRes) ? krsRes : (krsRes?.data || []));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditForm({
      description: objective.description,
      status: objective.status
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await api.put(`/objectives/${id}`, editForm);
      setEditing(false);
      await loadData();
      alert('✅ Objetivo actualizado correctamente');
    } catch (error) {
      console.error('Error updating objective:', error);
      alert('Error al actualizar el objetivo: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditForm({
      description: objective.description,
      status: objective.status
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  if (!objective) {
    return <div className="text-center py-12">Objetivo no encontrado</div>;
  }

  const progress = keyResults.length > 0
    ? keyResults.reduce((sum, kr) => sum + (parseFloat(kr.progress_percentage) || 0), 0) / keyResults.length
    : 0;

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/objetivos')}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Volver</span>
      </button>

      {/* Header with edit */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-4">
          {editing ? (
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="active">Activo</option>
                  <option value="completed">Completado</option>
                  <option value="on_hold">En Pausa</option>
                  <option value="archived">Archivado</option>
                </select>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                  <span>Guardar</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  <X className="w-4 h-4" />
                  <span>Cancelar</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-2">
                  {objective.period || 'Sin período'}
                </div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {objective.description}
                </h1>
                <div className="mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    objective.status === 'active' ? 'bg-green-100 text-green-800' :
                    objective.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    objective.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {objective.status === 'active' ? 'Activo' :
                     objective.status === 'completed' ? 'Completado' :
                     objective.status === 'on_hold' ? 'En Pausa' :
                     'Archivado'}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-4">
                <button
                  onClick={handleEdit}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <div className="text-right">
                  <div className="text-4xl font-bold text-indigo-600">
                    {progress.toFixed(1)}%
                  </div>
                  <p className="text-sm text-gray-600">Progreso</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Key Results section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Key Results ({keyResults.length})</h2>
          <Link
            to={`/objetivos/${id}/kr/nuevo`}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            <span>Nuevo Key Result</span>
          </Link>
        </div>

        {keyResults.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No hay Key Results aún. Crea el primero para comenzar a medir el progreso.
          </p>
        ) : (
          <div className="space-y-4">
            {keyResults.map((kr) => (
              <Link
                key={kr.id}
                to={`/objetivos/${id}/kr/${kr.id}`}
                className="block p-4 border border-gray-200 rounded-lg hover:border-indigo-500 hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900">{kr.description}</h3>
                  <span className="text-2xl font-bold text-indigo-600">
                    {parseFloat(kr.progress_percentage || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Responsable: {kr.owner_name || 'Sin asignar'}</span>
                  <span>{kr.current_value} / {kr.target_value} {kr.metric_name}</span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(parseFloat(kr.progress_percentage || 0), 100)}%` }}
                  />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjetivoDetailPage;
